import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Calendar,
  Clock,
  Users,
  MapPin,
  Check,
  X,
  Save,
  AlertCircle,
  UserCheck,
  UserX,
  FileText
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useBusiness } from '../../contexts/BusinessContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Loading } from '../../components/ui/Loading';
import { toast } from 'react-hot-toast';

interface EnrolledUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  profilePhotoUrl?: string;
}

interface ClassData {
  id: string;
  name: string;
  date: string;
  time: string;
  endTime?: string;
  enrolled: Record<string, boolean>; // Solo guarda userId: boolean
  maxCapacity: number;
  location?: string;
  instructorId: string;
  notes?: string;
  attendance?: Record<string, boolean>;
}

const InstructorClassDetail: React.FC = () => {
  const { classId } = useParams<{ classId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { businessId } = useBusiness();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [classData, setClassData] = useState<ClassData | null>(null);
  const [enrolledUsers, setEnrolledUsers] = useState<EnrolledUser[]>([]);
  const [attendance, setAttendance] = useState<Record<string, boolean>>({});
  const [notes, setNotes] = useState('');
  const [instructorId, setInstructorId] = useState<string | null>(null);

  useEffect(() => {
    if (businessId && currentUser) {
      loadInstructorAndClass();
    }
  }, [businessId, currentUser, classId]);

  const loadInstructorAndClass = async () => {
    if (!businessId || !currentUser || !classId) return;

    try {
      setLoading(true);

      // Verificar que el usuario es instructor de este negocio
      const instructorsQuery = query(
        collection(db, 'instructors'),
        where('businessId', '==', businessId),
        where('userId', '==', currentUser.id)
      );
      const instructorSnapshot = await getDocs(instructorsQuery);

      if (instructorSnapshot.empty) {
        toast.error('No tienes permisos para ver esta clase');
        navigate('/instructor');
        return;
      }

      const instId = instructorSnapshot.docs[0].id;
      setInstructorId(instId);

      // Cargar datos de la clase
      const classDoc = await getDoc(doc(db, 'classes', classId));
      
      if (!classDoc.exists()) {
        toast.error('Clase no encontrada');
        navigate('/instructor');
        return;
      }

      const data = { id: classDoc.id, ...classDoc.data() } as ClassData;

      // Verificar que esta clase pertenece a este instructor
      if (data.instructorId !== instId) {
        toast.error('Esta clase no está asignada a ti');
        navigate('/instructor');
        return;
      }

      setClassData(data);
      setAttendance(data.attendance || {});
      setNotes(data.notes || '');

      // Cargar datos de los usuarios inscritos
      const enrolledIds = Object.keys(data.enrolled || {}).filter(id => data.enrolled[id]);
      if (enrolledIds.length > 0) {
        const usersData: EnrolledUser[] = [];
        for (const userId of enrolledIds) {
          const userDoc = await getDoc(doc(db, 'users', userId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            usersData.push({
              id: userId,
              firstName: userData.firstName || '',
              lastName: userData.lastName || '',
              email: userData.email || '',
              profilePhotoUrl: userData.profilePhotoUrl
            });
          }
        }
        setEnrolledUsers(usersData);
      }

    } catch (error) {
      console.error('Error cargando clase:', error);
      toast.error('Error al cargar los datos de la clase');
    } finally {
      setLoading(false);
    }
  };

  const toggleAttendance = (odId: string) => {
    setAttendance(prev => ({
      ...prev,
      [odId]: !prev[odId]
    }));
  };

  const markAllPresent = () => {
    const newAttendance: Record<string, boolean> = {};
    enrolledUsers.forEach(user => {
      newAttendance[user.id] = true;
    });
    setAttendance(newAttendance);
  };

  const markAllAbsent = () => {
    const newAttendance: Record<string, boolean> = {};
    enrolledUsers.forEach(user => {
      newAttendance[user.id] = false;
    });
    setAttendance(newAttendance);
  };

  const saveAttendance = async () => {
    if (!classData || !classId) return;

    try {
      setSaving(true);
      await updateDoc(doc(db, 'classes', classId), {
        attendance,
        notes,
        updatedAt: serverTimestamp()
      });
      toast.success('Asistencia guardada correctamente');
    } catch (error) {
      console.error('Error guardando asistencia:', error);
      toast.error('Error al guardar la asistencia');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('es-ES', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (time: string) => time.substring(0, 5);

  const isPastClass = () => {
    if (!classData) return false;
    const classDateTime = new Date(`${classData.date}T${classData.time}`);
    return classDateTime < new Date();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loading size="lg" />
      </div>
    );
  }

  if (!classData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Clase no encontrada</h2>
          <Button onClick={() => navigate('/instructor')}>Volver al inicio</Button>
        </div>
      </div>
    );
  }

  const presentCount = Object.values(attendance).filter(Boolean).length;
  const isClassPast = isPastClass();

  return (
    <div className="space-y-6 pb-20">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-xl bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-white">{classData.name}</h1>
          <p className="text-gray-400 capitalize">{formatDate(classData.date)}</p>
        </div>
      </div>

      {/* Info de la clase */}
      <Card className="p-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary-500/20 flex items-center justify-center">
              <Clock className="w-5 h-5 text-primary-400" />
            </div>
            <div>
              <p className="text-sm text-gray-400">Horario</p>
              <p className="font-medium text-white">
                {formatTime(classData.time)}
                {classData.endTime && ` - ${formatTime(classData.endTime)}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-sm text-gray-400">Inscritos</p>
              <p className="font-medium text-white">
                {enrolledUsers.length}/{classData.maxCapacity}
              </p>
            </div>
          </div>
          {classData.location && (
            <div className="flex items-center gap-3 col-span-2">
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Ubicación</p>
                <p className="font-medium text-white">{classData.location}</p>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Resumen de asistencia */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="p-4 text-center bg-green-500/10 border-green-500/30">
          <UserCheck className="w-6 h-6 text-green-400 mx-auto mb-2" />
          <div className="text-2xl font-bold text-green-400">{presentCount}</div>
          <div className="text-sm text-gray-400">Presentes</div>
        </Card>
        <Card className="p-4 text-center bg-red-500/10 border-red-500/30">
          <UserX className="w-6 h-6 text-red-400 mx-auto mb-2" />
          <div className="text-2xl font-bold text-red-400">{enrolledUsers.length - presentCount}</div>
          <div className="text-sm text-gray-400">Ausentes</div>
        </Card>
      </div>

      {/* Lista de alumnos */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Lista de Alumnos</h2>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={markAllPresent}>
              <Check className="w-4 h-4 mr-1" />
              Todos
            </Button>
            <Button variant="outline" size="sm" onClick={markAllAbsent}>
              <X className="w-4 h-4 mr-1" />
              Ninguno
            </Button>
          </div>
        </div>

        {enrolledUsers.length === 0 ? (
          <Card className="p-8 text-center">
            <Users className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500">No hay alumnos inscritos en esta clase</p>
          </Card>
        ) : (
          <div className="space-y-2">
            {enrolledUsers.map((user) => {
              const isPresent = attendance[user.id] === true;
              const isMarked = attendance[user.id] !== undefined;

              return (
                <Card
                  key={user.id}
                  className={`p-4 cursor-pointer transition-all ${
                    isPresent 
                      ? 'bg-green-500/10 border-green-500/30' 
                      : isMarked 
                        ? 'bg-red-500/10 border-red-500/30' 
                        : ''
                  }`}
                  onClick={() => toggleAttendance(user.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {user.profilePhotoUrl ? (
                        <img
                          src={user.profilePhotoUrl}
                          alt={`${user.firstName} ${user.lastName}`}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-300">
                            {user.firstName?.charAt(0) || '?'}
                          </span>
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-white">{user.firstName} {user.lastName}</p>
                        <p className="text-sm text-gray-400">{user.email}</p>
                      </div>
                    </div>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                      isPresent 
                        ? 'bg-green-500 text-white' 
                        : isMarked 
                          ? 'bg-red-500/20 text-red-400 border-2 border-red-500' 
                          : 'bg-gray-700 text-gray-400 border-2 border-gray-600'
                    }`}>
                      {isPresent ? (
                        <Check className="w-5 h-5" />
                      ) : isMarked ? (
                        <X className="w-5 h-5" />
                      ) : (
                        <span className="text-lg">?</span>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Notas de la clase */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <FileText className="w-5 h-5 text-primary-400" />
          <h2 className="text-lg font-semibold text-white">Notas de la Clase</h2>
        </div>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Añade notas sobre esta clase (incidencias, observaciones, etc.)"
          rows={4}
          className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
        />
      </div>

      {/* Botón guardar */}
      <Button
        onClick={saveAttendance}
        disabled={saving}
        className="w-full"
        size="lg"
      >
        <Save className="w-5 h-5 mr-2" />
        {saving ? 'Guardando...' : 'Guardar Asistencia y Notas'}
      </Button>

      {/* Indicador de clase pasada */}
      {isClassPast && (
        <p className="text-center text-sm text-amber-400">
          ⚠️ Esta clase ya ha pasado. Puedes editar la asistencia retroactivamente.
        </p>
      )}
    </div>
  );
};

export default InstructorClassDetail;

